import Canvas from "./canvas";
import {VRInformationModeEnum} from "./../VRMessageLiterals"

class VRMessage extends Canvas {
    configData;
    duration;
    message;
    position;
    quaternion;
    scaleMultiplier;
    messageId;
    isCameraFix;
    parent;
    spacing;
    isSceneFollow;
    sceneFollowPosition;
    sceneFollowScale;

    constructor(properties) {
        super(properties);
        this.configData = properties;
        this.duration = Math.min(properties.duration || 5000, MAX_SIGNED_INT_32);
        if (properties.infiniteDuration) {
            this.duration = MAX_SIGNED_INT_32;
        }

        this.message = this.createVRMessage();
        this.messageId = properties.messageId;
        this.parent = properties.parent || iq3Camera.camera;
        this.position = {};
        this.quaternion = properties.Scene?.quaternion?.map(parseFloat) || [];
        this.scaleMultiplier = properties.Scene?.scaleMultiplier || properties.scaleMultiplier || 1.0;
        this.sceneFollowScale = properties.Scene?.sceneFollowScale || properties.sceneFollowScale || 1.0;
        this.spacing = properties.spacing;
        this.sceneFollowPosition = properties.Scene?.sceneFollowPosition || properties.sceneFollowPosition || "Default";

    }

    setPosition(position) {
        this.setVRMessageParent((isVRConnected() && !isMobileARModeEnabled()) ? this.configData.VRMode : this.configData.NonVRMode);
        this.updateScalingAndPosition(position);
        if (this.quaternion.length && !this.isCameraFix) {
            const quaternion = new THREE.Quaternion().fromArray(this.quaternion);
            this.message.object.quaternion.copy(quaternion);
        }
    }

    updateScalingAndPosition(position) {
        this.position = {
            xPosition: position.xPosition,
            yPosition: position.yPosition,
            zPosition: position.zPosition
        };
        this.updateScaling();
        this.updatePosition();
    }

    updateScaling() {
        let factor = this.message.object.scale_width / tooltip_default_scale;
        this.message.object.scaleFactor = factor;
        const fovScaleFactor = getFovScaleFactor();
        if (this.isCameraFix) {
            this.scaleMultiplier = 1.0;
        }

        if (this.isSceneFollow) {
            this.message.object.scale.set(
                factor * this.sceneFollowScale * fovScaleFactor,
                (factor * this.sceneFollowScale * this.message.object.scale_height * fovScaleFactor) / this.message.object.scale_width,
                this.sceneFollowScale
            );

            return;
        }

        this.message.object.scale.set(
            factor * this.scaleMultiplier * fovScaleFactor,
            (factor * this.scaleMultiplier * this.message.object.scale_height * fovScaleFactor) / this.message.object.scale_width,
            this.scaleMultiplier
        );
    }

    setDuration(duration) {
        this.duration = duration;
    }

    updatePosition() {
        this.message.object.position.set(this.position.xPosition, this.getYPosition(), this.position.zPosition);
    }

    getYPosition() {
        return this.position.yPosition * getFovScaleFactor();
    }

    getMessageHeight() {
        const dimension3DFactor = 200;
        return this.canvasHeight / dimension3DFactor;
    }

    show() {
        this.fadeIn();
    }

    adjustFovChange() {
        const fovScaleFactor = getFovScaleFactor();
        this.message.object.position.set(0, this.getYPosition() + this.spacing, this.position.zPosition);
        this.message.object.scale.set(
            this.message.object.scaleFactor * fovScaleFactor,
            (this.message.object.scaleFactor * fovScaleFactor * this.message.object.scale_height) / this.message.object.scale_width,
            fovScaleFactor);
    }

    updatePositionY() {
        const initialY = this.message.object.position.y;
        const targetY = this.getYPosition();
        const animationDuration = 500;

        new TWEEN.Tween({y: initialY})
            .to({y: targetY}, animationDuration)
            .onUpdate(({y}) => {
                this.message.object.position.set(this.position.xPosition, y, this.position.zPosition);
            })
            .start();
    }

    fadeOut() {
        const material = this.message.object.material;
        material.transparent = true;
        material.opacity = 1;

        const removeMessage = () => {
            this.parent.remove(this.message.object);
            disposeObject(this.message.object);
        }

        new TWEEN.Tween(material)
            .to({opacity: 0}, 500)
            .onUpdate(() => {
                material.needsUpdate = true;
            })
            .onComplete(() => {
                removeMessage()
            })
            .onStop(() => {
                removeMessage()
            })
            .start();
    }

    hideMessage() {
        this.parent.remove(this.message.object);
    }

    showMessage() {
        this.parent.add(this.message.object);
    }


    fadeIn() {
        this.parent.add(this.message.object);
        const material = this.message.object.material;
        material.transparent = true;
        material.opacity = 0;
        new TWEEN.Tween(material)
            .to({opacity: this.opacity}, 300)
            .onUpdate(() => {
                material.needsUpdate = true;
            })
            .start();
    }

    setVRMessageParent(mode) {
        mode = mode ?? "";

        switch (mode.toLowerCase()) {
            case VRInformationModeEnum.SCENE_FIX:
                this.setParentObject(VR.scene);
                this.isCameraFix = false;
                this.isSceneFollow = false;
                break;
            case VRInformationModeEnum.SCENE_FOLLOW:
                this.setParentObject(cameraFollower);
                this.isCameraFix = false;
                this.isSceneFollow = true;
                break;
            case VRInformationModeEnum.CAMERA_FIX:
                this.isCameraFix = true;
                this.isSceneFollow = false;
                this.setParentObject(iq3Camera.camera);
                break;
            default:
                this.isCameraFix = true;
                this.isSceneFollow = false;
                this.setParentObject(iq3Camera.camera);
        }
    }

    setParentObject(parent) {
        this.parent = parent;
    }

    disposeMessage() {
        this.fadeOut();
    }

    disposeMessageWithoutFadeOut() {
        this.parent.remove(this.message.object);
        disposeObject(this.message.object);
    }
}

export default VRMessage;